import type {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
  Store,
} from "vuex";
import type { State } from "@/store/state";
import type {
  ActivitiesPaginated,
  Activity,
  ActivityUpdate,
  Alarm,
} from "@/interfaces/activity";
import { eventMonitor } from "@/api/eventMonitor";
import { demonstrationActivities } from "@/store/activities/demonstration";
import {
  getIcon,
  getIconSVG,
  OTHER_UNCLASSIFIED_ICON,
  OTHER_UNCLASSIFIED_ICON_SVG,
  OTHER_UNCLASSIFIED_COLOR,
} from "@/utils";
import { DateTime } from "luxon";
import i18n from "@/i18n";

export interface ActivitiesState {
  activities: Activity[];
  filteredActivities: ActivitiesPaginated;
  selectedActivity: Activity | null;
}

const defaultState: ActivitiesState = {
  activities: [],
  filteredActivities: { total: 0, items: [] },
  selectedActivity: null,
};

const getOpenActivities = (state: ActivitiesState): Activity[] =>
  state.activities.filter((activity: Activity) => {
    return activity.status.toLowerCase() === "open";
  });

// Getters
const getters: GetterTree<ActivitiesState, State> = {
  openActivities: (state): Activity[] => {
    return getOpenActivities(state);
  },

  alarms: (state): Alarm[] => {
    return getOpenActivities(state).map((activity: Activity) => {
      // An activity reported via the "Other" false-negative flow resolves
      // its classification to "Normal" (there's no real label for "Other"
      // to point to), so other_description takes priority - otherwise the
      // pin would be indistinguishable from a real non-event. The
      // null-initial_classification guard below is kept as a defensive
      // fallback for any other caller.
      const isOtherUnclassified = !!activity.other_description;
      const initialClassification = activity.initial_classification;
      return {
        activity_id: activity.id,
        icon_url: isOtherUnclassified
          ? OTHER_UNCLASSIFIED_ICON_SVG
          : getIconSVG(activity.classification),
        lat: activity.nearest_epicenter_location.lat,
        lon: activity.nearest_epicenter_location.lon,
        name: activity.name,
        address: activity.nearest_epicenter_location.address,
        classification: isOtherUnclassified
          ? (i18n.global.t("ui_elements.other_unclassified") as string)
          : initialClassification
            ? initialClassification.label
            : (i18n.global.t("ui_elements.other_unclassified") as string),
        start_time: DateTime.fromISO(activity.start_time).toLocaleString(),
        color: isOtherUnclassified
          ? OTHER_UNCLASSIFIED_COLOR
          : initialClassification
            ? initialClassification.color
            : OTHER_UNCLASSIFIED_COLOR,
        asset_id: activity.nearest_epicenter_location.section.asset_id,
        icon: isOtherUnclassified
          ? OTHER_UNCLASSIFIED_ICON
          : initialClassification
            ? getIcon(initialClassification)
            : OTHER_UNCLASSIFIED_ICON,
      };
    });
  },

  totalFilteredActivities: (state): number => {
    return state.filteredActivities.total;
  },

  filteredActivities: (state): Activity[] => {
    return state.filteredActivities.items;
  },

  selectedActivity: (state): Activity | null => {
    return state.selectedActivity;
  },
};

const NS = "activities/";

export const readOpenActivities = (store: Store<State>) =>
  store.getters[`${NS}openActivities`] as Activity[];
export const readAlarms = (store: Store<State>) =>
  store.getters[`${NS}alarms`] as Alarm[];
export const readTotalFilteredActivities = (store: Store<State>) =>
  store.getters[`${NS}totalFilteredActivities`] as number;
export const readFilteredActivities = (store: Store<State>) =>
  store.getters[`${NS}filteredActivities`] as Activity[];
export const readSelectedActivity = (store: Store<State>) =>
  store.getters[`${NS}selectedActivity`] as Activity | null;

// Mutations

const mutations: MutationTree<ActivitiesState> = {
  setActivities(state, activities: Activity[]): void {
    state.activities = activities;
  },
  setActivity(state, activity: Activity): void {
    const activities = state.activities.filter(
      (a: Activity) => a.id !== activity.id,
    );
    activities.push(activity);
    state.activities = activities;
  },
  setFilteredActivities(state, activities: ActivitiesPaginated): void {
    state.filteredActivities = {
      ...activities,
      items: [...activities.items],
    };
  },
  setSelectedActivity(state, activity: Activity): void {
    state.selectedActivity = activity;
  },
};

export const commitSetActivities = (
  store: Store<State>,
  payload: Activity[],
): void => {
  store.commit(`${NS}setActivities`, payload);
};

export const commitSetActivity = (
  store: Store<State>,
  payload: Activity,
): void => {
  store.commit(`${NS}setActivity`, payload);
};

export const commitSetFilteredActivities = (
  store: Store<State>,
  payload: ActivitiesPaginated,
): void => {
  store.commit(`${NS}setFilteredActivities`, payload);
};

export const commitSetSelectedActivity = (
  store: Store<State>,
  payload: Activity,
): void => {
  store.commit(`${NS}setSelectedActivity`, payload);
};

// Actions

type ActivitiesContext = ActionContext<ActivitiesState, State>;

const actions: ActionTree<ActivitiesState, State> = {
  async actionGetOpenActivities(context: ActivitiesContext): Promise<void> {
    try {
      if (
        context.rootState.siteConfig.demoMode &&
        context.rootState.main.userProfile?.can_acknowledge_activities
      ) {
        context.commit(
          "main/addNotification",
          {
            content: "Demo mode is enabled, using demonstration activities.",
            color: "info",
          },
          { root: true },
        );
        context.commit("setActivities", demonstrationActivities);
      } else {
        const response = await eventMonitor.getOpenActivities(
          context.rootState.main.token,
        );
        context.commit("setActivities", response.data);
      }
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },

  async actionUpdateActivity(
    context: ActivitiesContext,
    payload: { id: number; activity: ActivityUpdate },
  ): Promise<boolean> {
    try {
      if (context.rootState.siteConfig.demoMode) {
        // Update the activity, then commit the update
        const toUpdate = demonstrationActivities.find(
          (activity) => activity.id === payload.id,
        );
        const updated = { ...toUpdate, ...payload.activity };
        context.commit("setActivity", updated);
      } else {
        const response = await eventMonitor.updateActivity(
          context.rootState.main.token,
          payload.id,
          payload.activity,
        );
        context.commit("setActivity", response.data);
      }
      return true;
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
      return false;
    }
  },

  async actionAcknowledgeAllOpenActivities(
    context: ActivitiesContext,
  ): Promise<void> {
    try {
      if (context.rootState.siteConfig.demoMode) {
        // Acknowledge all open activities
        const acknowledgedActivities = demonstrationActivities.map(
          (activity) => {
            return { ...activity, status: "acknowledged" };
          },
        );
        context.commit("setActivities", acknowledgedActivities);
      } else {
        await eventMonitor.acknowledgeAllOpenActivities(
          context.rootState.main.token,
          {
            acknowledged_by: context.rootState.main.userProfile.email,
          },
        );
        context.commit("setActivities", []);
      }
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },

  async actionGetFilteredActivities(
    context: ActivitiesContext,
    payload: {
      skip?: number;
      limit?: number;
      sort_by?: string;
      sort_desc?: boolean;
      device_ids?: number[];
      asset_ids?: number[];
      statuses?: string[];
      label_ids?: string[];
      start_time?: string;
      end_time?: string;
    },
  ): Promise<void> {
    if (context.rootState.siteConfig.demoMode) {
      let activities = [...demonstrationActivities];

      if (payload.device_ids.length) {
        activities = activities.filter((activity: Activity) =>
          payload.device_ids.includes(activity.locations[0].device_id),
        );
      }
      if (payload.asset_ids.length) {
        activities = activities.filter((activity: Activity) =>
          payload.asset_ids.includes(
            activity.nearest_epicenter_location.section.asset_id,
          ),
        );
      }
      if (payload.statuses.length) {
        activities = activities.filter((activity: Activity) =>
          payload.statuses.includes(activity.status),
        );
      }
      if (payload.label_ids.length) {
        activities = activities.filter((activity: Activity) =>
          payload.label_ids.includes(activity.classification.id.toString()),
        );
      }
      if (payload.start_time && payload.end_time) {
        activities = activities.filter((activity: Activity) => {
          return (
            DateTime.fromISO(payload.start_time) <=
              DateTime.fromISO(activity.end_time) &&
            DateTime.fromISO(activity.start_time) <=
              DateTime.fromISO(payload.end_time)
          );
        });
      }

      activities.sort((a, b) => (a.start_time > b.start_time ? 1 : -1));

      context.commit("setFilteredActivities", {
        items: activities,
        total: demonstrationActivities.length,
      });
    } else {
      const response = await eventMonitor.getActivities(
        context.rootState.main.token,
        payload,
      );
      context.commit("setFilteredActivities", response.data);
    }
  },

  async actionGetSelectedActivity(
    context: ActivitiesContext,
    id: number,
  ): Promise<void> {
    try {
      if (context.rootState.siteConfig.demoMode) {
        const activity = demonstrationActivities.find(
          (activity) => activity.id === id,
        );
        context.commit("setSelectedActivity", activity);
      } else {
        const response = await eventMonitor.getActivity(
          context.rootState.main.token,
          id,
        );
        context.commit("setSelectedActivity", response.data);
      }
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
};

export const dispatchActionGetOpenActivities = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetOpenActivities`);
export const dispatchActionUpdateActivity = (
  store: Store<State>,
  payload: { id: number; activity: ActivityUpdate },
) => store.dispatch(`${NS}actionUpdateActivity`, payload);
export const dispatchActionAcknowledgeAllOpenActivities = (
  store: Store<State>,
) => store.dispatch(`${NS}actionAcknowledgeAllOpenActivities`);
export const dispatchActionGetFilteredActivities = (
  store: Store<State>,
  payload: {
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_desc?: boolean;
    device_ids?: number[];
    asset_ids?: number[];
    statuses?: string[];
    label_ids?: string[];
    start_time?: string;
    end_time?: string;
  },
) => store.dispatch(`${NS}actionGetFilteredActivities`, payload);
export const dispatchActionGetSelectedActivity = (
  store: Store<State>,
  payload: number,
) => store.dispatch(`${NS}actionGetSelectedActivity`, payload);

// Module

export const activitiesModule: Module<ActivitiesState, State> = {
  namespaced: true,
  state: defaultState,
  mutations,
  actions,
  getters,
};
